import moment from "moment";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import {
  addPrices,
  displayBookingTimeRange,
  displayLocation,
  displayPrice,
  getOptionTitle,
  getVenuePrimaryPhotoUrl,
  subtractPrices
} from "sweet-global";
import CalendarIcon from "sweet-web/components/icons/CalendarIcon";
import ClockIcon from "sweet-web/components/icons/ClockIcon";
import DescriptionIcon from "sweet-web/components/icons/DescriptionIcon";
import GuestsIcon from "sweet-web/components/icons/GuestsIcon";
import TextButton from "sweet-web/components/universal/Button/TextButton";
import { fadeIn } from "sweet-web/lib/animations";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import {
  editBooking,
  loadEditableBooking
} from "sweet-web/redux/booking/actions";
import * as selectors from "sweet-web/redux/selectors";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  margin: 15px 0;
  max-width: 300px;
`;

const Section = styled.div<{ position: "TOP" | "MIDDLE" | "BOTTOM" }>`
  border: 1px solid ${colors.lightGray};
  border-top-left-radius: ${props => (props.position === "TOP" ? 5 : 0)}px;
  border-top-right-radius: ${props => (props.position === "TOP" ? 5 : 0)}px;
  border-bottom-left-radius: ${props =>
    props.position === "BOTTOM" ? 5 : 0}px;
  border-bottom-right-radius: ${props =>
    props.position === "BOTTOM" ? 5 : 0}px;
  border-bottom: ${props =>
    props.position === "BOTTOM" ? `1px solid ${colors.lightGray}` : "0"};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const BookedVenueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const BookedVenueText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const BookedVenueTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nearBlack};
  margin-bottom: 3px;
`;

const BookedVenueAddress = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.gray};
`;

const BookedVenueImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  object-fit: cover;
`;

const DataLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const DataText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.nearBlack};
  margin-left: 8px;
  margin-bottom: 3px;
`;

const VisualData = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const VisualDataText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.nearBlack};
  margin-left: 8px;
`;

const VisualDataImage = styled.img`
  width: 80px;
  height: 40px;
  border-radius: 5px;
`;

const PriceLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const PriceLine = styled.div<{ top?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: ${props => (props.top ? "flex-start" : "center")};
  margin-top: 15px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const PriceLabel = styled.p<{ bold?: boolean }>`
  font-size: 14px;
  font-weight: ${props => (props.bold ? 600 : 500)};
  color: ${colors.nearBlack};
`;

const PriceSmallValue = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.nearBlack};
`;

const PriceValue = styled.p<{ bold?: boolean }>`
  font-size: 14px;
  font-weight: ${props => (props.bold ? 600 : 500)};
  color: ${colors.nearBlack};
`;

const PriceSubLine = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const PriceSubLabel = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.gray};
`;

const PriceSubConnector = styled.div`
  height: 1px;
  background-color: ${colors.lighterGray};
  margin: 0 5px;
  flex: 1;
`;

const PriceSubValue = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.gray};
`;

type OwnProps = {
  bookingId: string;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

const BookingComponent: React.FC<Props> = props => {
  const { bookingId, venue, userType, booking } = props;
  const [priceExpanded, setPriceExpanded] = useState(false);

  if (!booking) return null;
  if (booking.status === "INQUIRY") return null;

  const venuePrimaryPhotoUrl = getVenuePrimaryPhotoUrl(venue);

  const togglePriceExpanded = () => setPriceExpanded(!priceExpanded);

  return (
    <Wrapper>
      <Section position={"TOP"}>
        <BookedVenueWrapper>
          <BookedVenueText>
            <BookedVenueTitle>{venue.name}</BookedVenueTitle>
            <BookedVenueAddress>
              {displayLocation(venue.location)}
            </BookedVenueAddress>
          </BookedVenueText>
          {venuePrimaryPhotoUrl && (
            <BookedVenueImage src={venuePrimaryPhotoUrl} />
          )}
        </BookedVenueWrapper>
      </Section>
      <Section position={"MIDDLE"}>
        <DataLine>
          <CalendarIcon color={colors.gray} size={14} />
          <DataText>{moment(booking.date).format("ddd, MMM D")}</DataText>
        </DataLine>
        <DataLine>
          <GuestsIcon color={colors.gray} size={14} />
          <DataText>{booking.guests} Guests</DataText>
        </DataLine>
        <DataLine>
          <ClockIcon color={colors.gray} size={14} />
          <DataText>{displayBookingTimeRange(booking)}</DataText>
        </DataLine>
        {booking.comments && (
          <DataLine>
            <DescriptionIcon color={colors.gray} size={14} />
            <DataText>{booking.comments}</DataText>
          </DataLine>
        )}
        {Object.keys(booking.spaces).forEach(spaceId => {
          const space = venue.spaces[spaceId];

          return (
            <VisualData key={`space-${spaceId}`}>
              <VisualDataText>{space.name}</VisualDataText>
              <VisualDataImage src={getVenuePrimaryPhotoUrl(venue, spaceId)} />
            </VisualData>
          );
        })}
        {Object.keys(booking.options).forEach(optionId => {
          const option = venue.options[optionId];

          return (
            <VisualData key={`option-${optionId}`}>
              <VisualDataText>{getOptionTitle(option)}</VisualDataText>
              <VisualDataImage src={getVenuePrimaryPhotoUrl(venue, optionId)} />
            </VisualData>
          );
        })}
      </Section>
      <Section position={"MIDDLE"} onClick={togglePriceExpanded}>
        {!priceExpanded && (
          <PriceLine top={true}>
            <PriceLeft>
              <PriceLabel>Total (USD)</PriceLabel>
              <TextButton
                style={{ padding: 0, fontSize: 12 }}
                title={"See details"}
                color={colors.primary}
                noHover={true}
              />
            </PriceLeft>
            <PriceValue>{displayPrice(booking.total)}</PriceValue>
          </PriceLine>
        )}
        {priceExpanded && (
          <React.Fragment>
            <PriceLine>
              <PriceLabel>Venue Rental</PriceLabel>
              <PriceSmallValue>{displayPrice(booking.rental)}</PriceSmallValue>
            </PriceLine>
            <PriceLine>
              <PriceLabel>Taxes {"&"} Fees</PriceLabel>
              <PriceSmallValue>
                {displayPrice(
                  addPrices(Object.values(booking.fees).map(fee => fee.price))
                )}
              </PriceSmallValue>
            </PriceLine>
            {typeof booking.fees === "object" &&
              Object.keys(booking.fees).map((feeName, i) => {
                const fee = booking.fees[feeName];

                return (
                  <PriceSubLine key={`booking-fee-${i}`}>
                    <PriceSubLabel>{fee.title}</PriceSubLabel>
                    <PriceSubConnector />
                    <PriceSubValue>{displayPrice(fee.price)}</PriceSubValue>
                  </PriceSubLine>
                );
              })}
            <PriceLine>
              <PriceLabel>Total Price</PriceLabel>
              <PriceValue>{displayPrice(booking.total)}</PriceValue>
            </PriceLine>
          </React.Fragment>
        )}
      </Section>
      <Section position={"BOTTOM"}>
        <PriceLine>
          <PriceLabel bold={true}>Deposit Due Now</PriceLabel>
          <PriceValue bold={true}>{displayPrice(booking.deposit)}</PriceValue>
        </PriceLine>
        <PriceLine>
          <PriceLabel>
            Due {moment(booking.date).format("ddd, MMM D")}
          </PriceLabel>
          <PriceValue>
            {displayPrice(subtractPrices(booking.total, booking.deposit))}
          </PriceValue>
        </PriceLine>
      </Section>
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: OwnProps) => {
  return {
    booking: selectors.getBooking(ownProps.bookingId)(state),
    venue: selectors.getVenueByBooking(ownProps.bookingId)(state)(state),
    userType: selectors.userType(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      loadEditableBooking,
      editBooking
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(BookingComponent);
